// ===============================================
// Suppliers Management Page (Updated)
// หน้าจัดการผู้จำหน่าย - ใช้งานกับตาราง contacts
// ===============================================

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Tag,
  Row,
  Col,
  Statistic,
  Radio,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  DollarOutlined,
  TeamOutlined,
  CreditCardOutlined,
  PhoneOutlined,
  MailOutlined,
} from '@ant-design/icons';
import {
  suppliersAPI,
  formatCurrency,
  formatDate,
  handleAPIError
} from '../../services/PurchaseService';

const { Option } = Select;
const { Search } = Input;
const { TextArea } = Input;

const SuppliersManagement = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  // สถิติ
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    credit: 0,
    totalCreditLimit: 0,
  });

  // ดึงข้อมูลผู้จำหน่าย (จาก contacts)
  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchText,
      };
      const response = await suppliersAPI.getAll(params);
      if (response.success) {
        setSuppliers(response.data);
        calculateStatistics(response.data);
      }
    } catch (error) {
      message.error(handleAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  // คำนวณสถิติ
  const calculateStatistics = (data) => {
    const stats = {
      total: data.length,
      active: data.filter(s => s.status === 'active').length,
      credit: data.filter(s => s.payment_terms > 0).length,
      totalCreditLimit: data.reduce((sum, s) => sum + (s.credit_limit || 0), 0),
    };
    setStatistics(stats);
  };

  // เปิด Modal สำหรับเพิ่ม/แก้ไข
  const showModal = (supplier = null) => {
    setEditingSupplier(supplier);
    setModalVisible(true);
    
    if (supplier) {
      // แก้ไข - ตั้งค่าจากข้อมูล contact ที่มีอยู่
      form.setFieldsValue(supplier);
    } else {
      // เพิ่มใหม่ - ตั้งค่าเริ่มต้น
      form.setFieldsValue({
        type: 'supplier',
        status: 'active',
        payment_terms: 0,
        credit_limit: 0,
      });
    }
  };

  // ปิด Modal
  const handleCancel = () => {
    setModalVisible(false);
    setEditingSupplier(null);
    form.resetFields();
  };

  // บันทึกข้อมูล
  const handleSubmit = async (values) => {
    try {
      if (editingSupplier) {
        await suppliersAPI.update(editingSupplier.id, values);
        message.success('อัพเดตข้อมูลผู้จำหน่ายสำเร็จ');
      } else {
        await suppliersAPI.create(values);
        message.success('เพิ่มผู้จำหน่ายใหม่สำเร็จ');
      }
      
      handleCancel();
      fetchSuppliers();
    } catch (error) {
      message.error(handleAPIError(error));
    }
  };

  // ลบข้อมูล
  const handleDelete = async (id) => {
    try {
      await suppliersAPI.delete(id);
      message.success('ลบข้อมูลผู้จำหน่ายสำเร็จ');
      fetchSuppliers();
    } catch (error) {
      message.error(handleAPIError(error));
    }
  };

  // ค้นหา
  const handleSearch = (value) => {
    setSearchText(value);
  };

  // สี tag สำหรับสถานะ
  const getStatusColor = (status) => {
    return status === 'active' ? 'green' : 'red';
  };

  // คอลัมน์ของตาราง
  const columns = [
    {
      title: 'ชื่อ/ชื่อบริษัท',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'ประเภท',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => {
        const colors = {
          'supplier': 'blue',
          'customer': 'green',
          'both': 'purple',
        };
        const texts = {
          'supplier': 'ผู้จำหน่าย',
          'customer': 'ลูกค้า',
          'both': 'ทั้งสอง',
        };
        return (
          <Tag color={colors[type]}>
            {texts[type] || type}
          </Tag>
        );
      },
      filters: [
        { text: 'ผู้จำหน่าย', value: 'supplier' },
        { text: 'ทั้งสอง', value: 'both' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'เบอร์โทรศัพท์',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (phone) => phone ? (
        <Space>
          <PhoneOutlined />
          {phone}
        </Space>
      ) : '-',
    },
    {
      title: 'อีเมล',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (email) => email ? (
        <Space>
          <MailOutlined />
          {email}
        </Space>
      ) : '-',
    },
    {
      title: 'เงื่อนไขการชำระ',
      dataIndex: 'payment_terms',
      key: 'payment_terms',
      width: 120,
      align: 'center',
      render: (terms) => (
        <Tag color={terms > 0 ? 'orange' : 'green'}>
          {terms > 0 ? `${terms} วัน` : 'เงินสด'}
        </Tag>
      ),
      sorter: (a, b) => a.payment_terms - b.payment_terms,
    },
    {
      title: 'วงเงินเชื่อ',
      dataIndex: 'credit_limit',
      key: 'credit_limit',
      width: 150,
      align: 'right',
      render: (amount) => amount > 0 ? formatCurrency(amount) : '-',
      sorter: (a, b) => a.credit_limit - b.credit_limit,
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status === 'active' ? 'ใช้งาน' : 'ไม่ใช้งาน'}
        </Tag>
      ),
      filters: [
        { text: 'ใช้งาน', value: 'active' },
        { text: 'ไม่ใช้งาน', value: 'inactive' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'การดำเนินการ',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => showModal(record)}
          >
            แก้ไข
          </Button>
          <Popconfirm
            title="แน่ใจหรือไม่?"
            description="คุณต้องการลบผู้จำหน่ายนี้หรือไม่?"
            onConfirm={() => handleDelete(record.id)}
            okText="ลบ"
            cancelText="ยกเลิก"
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
            >
              ลบ
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // เรียกใช้เมื่อ component mount
  useEffect(() => {
    fetchSuppliers();
  }, []);

  // เรียกใช้เมื่อค้นหาเปลี่ยน
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchSuppliers();
    }, 500); // debounce search

    return () => clearTimeout(delayedSearch);
  }, [searchText]);

  return (
    <div className="suppliers-management">
      {/* สถิติ */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={6}>
          <Card>
            <Statistic
              title="ผู้จำหน่ายทั้งหมด"
              value={statistics.total}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={6}>
          <Card>
            <Statistic
              title="ใช้งานอยู่"
              value={statistics.active}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={6}>
          <Card>
            <Statistic
              title="เงินเชื่อ"
              value={statistics.credit}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<CreditCardOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={6}>
          <Card>
            <Statistic
              title="วงเงินเชื่อรวม"
              value={statistics.totalCreditLimit}
              formatter={(value) => formatCurrency(value)}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* ตัวกรองและการค้นหา */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={16} lg={18}>
            <Search
              placeholder="ค้นหาชื่อผู้จำหน่าย เบอร์โทรศัพท์ หรืออีเมล"
              allowClear
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} lg={6}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchSuppliers()}
              >
                รีเฟรช
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showModal()}
              >
                เพิ่มผู้จำหน่าย
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ตารางข้อมูล */}
      <Card>
        <Table
          columns={columns}
          dataSource={suppliers}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </Card>

      {/* Modal เพิ่ม/แก้ไขผู้จำหน่าย */}
      <Modal
        title={editingSupplier ? 'แก้ไขข้อมูลผู้จำหน่าย' : 'เพิ่มผู้จำหน่ายใหม่'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={handleCancel}
        okText={editingSupplier ? 'อัพเดต' : 'เพิ่ม'}
        cancelText="ยกเลิก"
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="ชื่อ/ชื่อบริษัท"
                name="name"
                rules={[{ required: true, message: 'กรุณาระบุชื่อ' }]}
              >
                <Input placeholder="ชื่อผู้จำหน่าย หรือชื่อบริษัท" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="ประเภท"
                name="type"
                rules={[{ required: true, message: 'กรุณาเลือกประเภท' }]}
              >
                <Radio.Group>
                  <Radio value="supplier">ผู้จำหน่าย</Radio>
                  <Radio value="both">ทั้งสอง</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="เบอร์โทรศัพท์"
                name="phone"
              >
                <Input placeholder="หมายเลขโทรศัพท์" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="อีเมล"
                name="email"
                rules={[{ type: 'email', message: 'รูปแบบอีเมลไม่ถูกต้อง' }]}
              >
                <Input placeholder="ที่อยู่อีเมล" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="ที่อยู่"
                name="address"
              >
                <TextArea rows={2} placeholder="ที่อยู่ที่ติดต่อ" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="เงื่อนไขการชำระ (วัน)"
                name="payment_terms"
                rules={[{ required: true, message: 'กรุณาระบุเงื่อนไข' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="0 = เงินสด"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="วงเงินเชื่อ (฿)"
                name="credit_limit"
                rules={[{ required: true, message: 'กรุณาระบุวงเงินเชื่อ' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1000}
                  formatter={(value) => value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                  parser={(value) => value.replace(/,/g, '')}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="สถานะ"
                name="status"
                rules={[{ required: true, message: 'กรุณาเลือกสถานะ' }]}
              >
                <Select placeholder="เลือกสถานะ">
                  <Option value="active">ใช้งาน</Option>
                  <Option value="inactive">ไม่ใช้งาน</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="หมายเหตุ"
                name="notes"
              >
                <TextArea rows={2} placeholder="หมายเหตุเพิ่มเติม" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default SuppliersManagement;